import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import {
  ArrowLeft,
  Plane,
  FileText,
  MapPin,
  ArrowRight,
  Plus,
  Trash2,
  Check,
  Calendar,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import { toast } from "sonner";
import { FullScreenLoading } from "components/ui/loader";
import {
  getTourByPublicId,
  getVenues,
  TourDetailApi,
  updateTour,
  UpdateTourPayload,
} from "services/tourService";
import { CustomDayPicker } from "ui/CustomDayPicker";
import {
  calculateVenueSchedule,
  getIntercityCheckout,
  getTotalVenueBuffer,
  getTravelTimeMinutes,
  normalizeTime,
  parseTimeToMinutes,
} from "utils/venueScheduleCalculator";
import {
  BufferConfig,
  getBufferConfigurations,
  VenueApi,
  PortApi,
  getPorts,
  getCutoffConfigurations,
  CutoffConfig,
  getPortByPublicId,
  getTourOperatorWithDocuments,
  IntercityCheckoutApi,
  getIntercityCheckouts,
} from "services/tourService";
import { useAuth } from "contexts/AuthContext";
import { Switch } from "ui/switch";

interface TourRecommendChangesProps {
  onNavigate?: (page: string) => void;
  tourId?: string;
}

interface FormData {
  tourType: string;
  packageId?: string;
  packageName?: string;
  parentTourId: string;
  arrivalTransportMode: "flight" | "road" | "sea";
  arrivalAirport: number;
  arrivalDate: string;
  arrivalTime: string;
  arrivalFlightNumber: string;
  arrivalBookingRef: string;
  arrivalAirline: string;
  arrivalConfirmed: boolean;
  departureTransportMode: "flight" | "road" | "sea";
  departureAirport: number;
  departureDate: string;
  departureTime: string;
  departureFlightNumber: string;
  departureBookingRef: string;
  departureAirline: string;
  departureConfirmed: boolean;
  tourName: string;
  noOfApplicants: string;
  faizAccommodation: "yes" | "no";
  accommodationPreference: "minimum" | "maximum";
  specialAccommodationRequest: string;
  venues: Array<{
    id: string;
    venueId: number | null;
    fromDate: string;
    fromTime: string;
    toDate: string;
    toTime: string;
  }>;
}

enum TourType {
  GENERAL = 1,
  MIQAAT = 2,
}

export default function TourRecommendChanges({
  onNavigate,
  tourId,
}: TourRecommendChangesProps) {
  const [activeTab, setActiveTab] = useState<
    "arrival-departure" | "venue-summary" | "confirmation"
  >("arrival-departure");
  const [approvalStatus, setApprovalStatus] = useState<
    "saved" | "requested" | "approved" | "recommended" | "re-requested"
  >("recommended");

  // Arrival state
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { id } = useParams();
  const [, setTourData] = useState<TourDetailApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setVenueOptions] = useState<{ id: string; name: string }[]>([]);
  const enabledTabs = ["arrival-departure", "venue-summary", "confirmation"];
  const currentIndex = enabledTabs.indexOf(activeTab);
  const [tourStatus, setTourStatus] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [venuesFromApi, setVenuesFromApi] = useState<VenueApi[]>([]);
  const [buffers, setBuffers] = useState<BufferConfig[]>([]);
  const [ports, setPorts] = useState<PortApi[]>([]);
  const [cutoffs, setCutoffs] = useState<CutoffConfig[]>([]);
  const [cutoffDates, setCutoffDates] = useState<string[]>([]);
  const [isVenueManuallyChanged, setIsVenueManuallyChanged] = useState(false);
  const [portBlocks, setPortBlocks] = useState<Record<number, Date[]>>({});
  const [operatorExpiryDate, setOperatorExpiryDate] = useState<Date | null>(
    null,
  );

  const [arrivalConfirmedFromApi, setArrivalConfirmedFromApi] = useState(false);
  const [departureConfirmedFromApi, setDepartureConfirmedFromApi] = useState(false);
  
  const [accommodateBy, setAccommodateBy] = useState<"minimum" | "maximum">(
    "maximum",
  );
  const [specialRoomRequestDetails, setSpecialRoomRequestDetails] =
    useState<string>("");

  const searchParams = new URLSearchParams(location.search);

  const previousBookingFilter =
  searchParams.get("bookingFilter") || "current";

  const portBlockedDates = Object.values(portBlocks).flat();
  const cutoffBlockedDates = cutoffDates.map((d) => {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day);
  });

  const allBlockedDates = [...portBlockedDates, ...cutoffBlockedDates];
  const tourOperatorId = user?.tourOperatorId ?? null;
  const [isInitialized, setIsInitialized] = useState(false);

  const getTourType = () => {
    return selectedMiqaat?.type === "package"
      ? TourType.MIQAAT
      : TourType.GENERAL;
  };

  const getTourCategory = () => {
    const tourType = getTourType();

    switch (tourType) {
      case TourType.GENERAL:
        return formData.faizAccommodation === "yes" ? 3 : 4;

      case TourType.MIQAAT:
        return 2;

      default:
        return null;
    }
  };

  const selectedMiqaat = location.state?.selectedMiqaat;
  const [intercityCheckouts, setIntercityCheckouts] = useState<
    IntercityCheckoutApi[]
  >([]);

  const [recommendedDates] = useState([
    {
      id: 1,
      arrivalDate: "2025-09-20",
      arrivalTime: "14:30",
      departureDate: "2025-09-29",
      departureTime: "09:15",
      reason: "Full room availability for all dates",
      roomsAvailable: {
        deluxe: 5,
        standard: 10,
        sharedGents: 8,
        sharedLadies: 6,
      },
    },
    {
      id: 2,
      arrivalDate: "2025-09-22",
      arrivalTime: "16:00",
      departureDate: "2025-10-01",
      departureTime: "10:00",
      reason: "Peak season availability with premium rooms",
      roomsAvailable: {
        deluxe: 3,
        standard: 12,
        sharedGents: 10,
        sharedLadies: 8,
      },
    },
    {
      id: 3,
      arrivalDate: "2025-09-25",
      arrivalTime: "15:30",
      departureDate: "2025-10-04",
      departureTime: "08:30",
      reason: "Extended stay option available",
      roomsAvailable: {
        deluxe: 4,
        standard: 8,
        sharedGents: 6,
        sharedLadies: 5,
      },
    },
  ]);
  const [selectedRecommendedDate, setSelectedRecommendedDate] = useState<
    number | null
  >(null);

  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const mapTransportMode = (mode: "flight" | "road" | "sea") => {
    if (mode === "flight") return 1;
    if (mode === "road") return 2;
    if (mode === "sea") return 3;
    return 1;
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isMiqaat = getTourType() === TourType.MIQAAT;

  const [formData, setFormData] = useState<FormData>({
    tourType: "",
    arrivalTransportMode: "flight",
    arrivalAirport: 0,
    arrivalDate: "",
    arrivalTime: "",
    arrivalFlightNumber: "",
    arrivalBookingRef: "",
    arrivalAirline: "",
    arrivalConfirmed: false,
    departureTransportMode: "flight",
    departureAirport: 0,
    departureDate: "",
    departureTime: "",
    departureFlightNumber: "",
    departureBookingRef: "",
    departureAirline: "",
    departureConfirmed: false,
    tourName: "",
    parentTourId: "",
    noOfApplicants: "",
    faizAccommodation: "no",
    accommodationPreference: "maximum",
    specialAccommodationRequest: "", 
    venues: [
      {
        id: "venue-1",
        venueId: null,
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
      {
        id: "venue-2",
        venueId: null,
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
    ],
  });

  const formatDateSafe = (dateStr?: string) => {
    if (!dateStr) return "";

    const [datePart] = dateStr.split("T");
    return datePart;
  };

  const buildUpdatePayload = (): UpdateTourPayload => {
    return {
      parent_id: formData.parentTourId || null,
      
      tourName: formData.tourName,
      tourType: getTourType(),
      tourCategory: getTourCategory(),
      rotationId: selectedMiqaat?.rotationId || null,
      tourOperatorId: user?.tourOperatorId || 0,
      noOfApplicants: Number(formData.noOfApplicants),
      faizAccommodation: formData.faizAccommodation === "yes",

      ...(formData.faizAccommodation === "yes" && {
        accommodationPreference:
          accommodateBy === "minimum" ? 1 : 2,

        specialAccommodationRequest:
          specialRoomRequestDetails,
      }),

      arrivalTransportMode: mapTransportMode(formData.arrivalTransportMode),
      arrivalFlightNo: formData.arrivalFlightNumber,
      arrivalTime: normalizeTime(formData.arrivalTime),
      arrivalPortId: formData.arrivalAirport,
      arrivalDate: `${formData.arrivalDate}T00:00:00`,
      arrivalBookingRef: formData.arrivalBookingRef,
      arrivalAirline: formData.arrivalAirline,

      departureTransportMode: mapTransportMode(formData.departureTransportMode),
      departureFlightNo: formData.departureFlightNumber,
      departureTime: normalizeTime(formData.departureTime),
      departurePortId: formData.departureAirport,
      departureDate: `${formData.departureDate}T00:00:00`,
      departureBookingRef: formData.departureBookingRef,
      departureAirline: formData.departureAirline,

      isArrivalConfirmed: formData.arrivalConfirmed,
      isDepartureConfirmed: formData.departureConfirmed,

      venueSchedules: formData.venues.map((v) => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        venueId: v.venueId!,
        checkInDate: `${v.fromDate}T00:00:00`,
        checkInTime: v.fromTime,
        checkOutDate: `${v.toDate}T00:00:00`,
        checkOutTime: v.toTime,
      })),

      updatedBy: 1,
    };
  };

  const handleAcceptRecommendedDate = (
    dateOption: (typeof recommendedDates)[0],
  ) => {
    setSelectedRecommendedDate(dateOption.id);

    setFormData((prev) => ({
      ...prev,

      arrivalDate: dateOption.arrivalDate,
      arrivalTime: dateOption.arrivalTime,

      departureDate: dateOption.departureDate,
      departureTime: dateOption.departureTime,
    }));

    setIsVenueManuallyChanged(false);

    toast.success(
      "Recommended dates applied to arrival and departure."
    );
  };

  useEffect(() => {
    const fetchTour = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const data = await getTourByPublicId(id!);
        console.log("FETCHED:", data);
        setTourData(data);
        mapApiToState(data);
      } catch (err) {
        toast.error("Failed to load tour");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await getVenues();

        setVenuesFromApi(res);

        const mapped = res.map((v) => ({
          id: String(v.venueId),
          name: v.venueName,
        }));

        setVenueOptions(mapped);
      } catch {
        toast.error("Failed to load venues");
      }
    };

    fetchVenues();
  }, []);

  const isConfirmed = formData.arrivalConfirmed || formData.departureConfirmed;

  useEffect(() => {
    if (!isInitialized) return;
    if (id) return;
    if (isConfirmed && !isVenueManuallyChanged) return;
    if (getTourType() === TourType.MIQAAT) return;
    if (formData.venues.some((v) => !v.venueId)) return;
    if (isVenueManuallyChanged) return;

    const updatedVenues = calculateVenueSchedule({
      venues: formData.venues,
      arrivalDate: formData.arrivalDate,
      arrivalTime: formData.arrivalTime,
      departureDate: formData.departureDate,
      departureTime: formData.departureTime,
      buffers,
      intercityCheckouts,
      tourType: getTourType(),
    });

    if (!updatedVenues) return;

    const isSame =
      JSON.stringify(formData.venues) === JSON.stringify(updatedVenues);

    if (isSame) return;

    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.map((v, i) => ({
        ...v,
        ...updatedVenues[i],
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    formData.arrivalDate,
    formData.arrivalTime,
    formData.departureDate,
    formData.departureTime,
    buffers,
    intercityCheckouts,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formData.venues),
  ]);

  useEffect(() => {
    const fetchBuffers = async () => {
      try {
        const data = await getBufferConfigurations();
        setBuffers(data.filter((b) => b.isActive));
      } catch (e) {
        console.error("Buffer fetch failed", e);
      }
    };

    fetchBuffers();
  }, []);

  useEffect(() => {
    const fetchAllPortBlocks = async () => {
      try {
        if (!ports.length) return;

        const results = await Promise.all(
          ports.map((port) => getPortByPublicId(port.publicId)),
        );

        const allBlocks: Record<number, Date[]> = {};

        results.forEach((result, index) => {
          const port = ports[index];

          const dates: Date[] = [];

          result.blocks.forEach((block) => {
            const start = new Date(block.fromDate.split("T")[0]);
            const end = new Date(block.toDate.split("T")[0]);

            // eslint-disable-next-line prefer-const
            let current = new Date(start);

            while (current <= end) {
              dates.push(
                new Date(
                  current.getFullYear(),
                  current.getMonth(),
                  current.getDate(),
                ),
              );
              current.setDate(current.getDate() + 1);
            }
          });

          allBlocks[port.portId] = dates;
        });

        setPortBlocks(allBlocks);
      } catch (e) {
        console.error(e);
      }
    };

    fetchAllPortBlocks();
  }, [ports]);

  useEffect(() => {
    const fetchOperator = async () => {
      try {
        if (!tourOperatorId) return;

        const res = await getTourOperatorWithDocuments(tourOperatorId);

        const expiry = res.partners?.find(
          (p) => p.isMainOperator,
        )?.permissionExpireDate;

        if (expiry) {
          const [datePart] = expiry.split(" ");

          const [mm, dd, yyyy] = datePart.split("/").map(Number);

          const parsed = new Date(yyyy, mm - 1, dd);

          setOperatorExpiryDate(parsed);
        }
      } catch (e) {
        console.error("Failed to fetch operator", e);
      }
    };

    fetchOperator();
  }, [tourOperatorId]);

  const getBaseRoute = (arrivalPortId: number) => {
    const najafVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("najaf"),
    )?.venueId;

    const karbalaVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("karbala"),
    )?.venueId;

    if (!najafVenue || !karbalaVenue) return [];

    const arrivalPort = ports.find((p) => p.portId === arrivalPortId);

    if (!arrivalPort) return [];

    const isNajafArrival = arrivalPort.portName.toLowerCase().includes("najaf");

    return isNajafArrival
      ? [najafVenue, karbalaVenue]
      : [karbalaVenue, najafVenue];
  };

  const [, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!isInitialized) return;
    if (formData.arrivalConfirmed) return;
    setIsVenueManuallyChanged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.arrivalAirport]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!venuesFromApi.length || !formData.arrivalAirport) return;
    if (formData.arrivalConfirmed) return;

    if (isVenueManuallyChanged) return;

    const route = getBaseRoute(formData.arrivalAirport);

    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.map((v, i) => ({
        ...v,
        venueId: route[i] || v.venueId,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    formData.arrivalAirport,
    venuesFromApi,
    isVenueManuallyChanged,
  ]);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const data = await getPorts();
        setPorts(data.filter((p) => p.isActive));
      } catch (e) {
        console.error("Port fetch failed", e);
      }
    };

    fetchPorts();
  }, []);

  useEffect(() => {
    const fetchCutoffs = async () => {
      try {
        const data = await getCutoffConfigurations();
        setCutoffs(data);
      } catch (e) {
        console.error("Cutoff fetch failed", e);
      }
    };

    fetchCutoffs();
  }, []);

  useEffect(() => {
    if (!cutoffs.length) return;

    const dates: string[] = [];

    cutoffs.forEach((c) => {
      const [year, month, day] = c.effectiveFrom
        .split("T")[0]
        .split("-")
        .map(Number);

      const start = new Date(year, month - 1, day);

      for (let i = 0; i < c.daysBeforeCutoff; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");

        dates.push(`${yyyy}-${mm}-${dd}`);
      }
    });

    setCutoffDates(dates);
  }, [cutoffs]);

  useEffect(() => {
    const fetchIntercity = async () => {
      try {
        const data = await getIntercityCheckouts();
        setIntercityCheckouts(data.filter((i) => i.isActive));
      } catch (e) {
        console.error("Intercity fetch failed", e);
      }
    };

    fetchIntercity();
  }, []);

  useEffect(() => {
    if (!selectedMiqaat) return;

    setFormData((prev) => {
      if (prev.arrivalDate || prev.departureDate) {
        return prev;
      }

      return {
        ...prev,
        arrivalDate: selectedMiqaat.arrivalDate?.split("T")[0] || "",
        departureDate: selectedMiqaat.departureDate?.split("T")[0] || "",
      };
    });
  }, [selectedMiqaat]);

  useEffect(() => {
    setIsVenueManuallyChanged(false);
  }, [formData.arrivalDate, formData.departureDate]);

  const getCalculationBreakdown = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    venue: any,
    index: number,
    type: "checkin" | "checkout",
  ) => {
    if (!venue.venueId) return null;

    const venueId = venue.venueId;

    const venueName =
      venuesFromApi.find((v) => v.venueId === venueId)?.venueName || "";

    const isLast = index === formData.venues.length - 1;

    let portId: number | undefined;

    if (index === 0) {
      portId = formData.arrivalAirport;
    } else if (isLast) {
      portId = formData.departureAirport;
    } else {
      portId = undefined;
    }

    let intercityTime: string | null = null;
    let intercityUsed = false;

    if (type === "checkout" && !isLast) {
      const nextVenue = formData.venues[index + 1];

      const intercity = nextVenue?.venueId
        ? getIntercityCheckout(
            intercityCheckouts,
            venueId,
            nextVenue.venueId,
            parseLocalDate(venue.toDate),
            getTourType(),
          )
        : null;

      if (intercity?.checkoutTime) {
        intercityTime = intercity.checkoutTime;
        intercityUsed = true;
      }

      // MIQAAT fallback (ONLY if no intercity from API)
      if (getTourType() === TourType.MIQAAT && !intercityUsed) {
        intercityTime = venue.toTime;
        intercityUsed = true;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMiqaat = getTourType() === TourType.MIQAAT;

    const isIntercityCase = type === "checkout" && !isLast && intercityUsed;

    let hasTravel = false;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity = getIntercityCheckout(
          intercityCheckouts,
          prevVenue.venueId,
          venue.venueId,
          parseLocalDate(prevVenue.toDate),
          getTourType(),
        );

        if (intercity?.travelTime) {
          hasTravel = true;
        }
      }
    }

    const buffersData = getTotalVenueBuffer(
      buffers,
      venueId,
      portId,
      !(isIntercityCase || hasTravel),
    );

    const accommodationBuffers = buffers.filter(
      (b) => b.venueId === venueId && b.type === "1",
    );

    const transportBuffers = !(isIntercityCase || hasTravel)
      ? buffers.filter((b) => {
          if (b.venueId !== venueId || b.type !== "2") return false;

          if (portId != null) {
            const selectedPort = ports.find((p) => p.portId === portId);

            return (
              Number(b.portId) === Number(portId) ||
              !b.portId ||
              (selectedPort &&
                b.portName &&
                selectedPort.portName.toLowerCase().trim() ===
                  b.portName.toLowerCase().trim())
            );
          }

          return true;
        })
      : [];

    const sumBuffers = (
      arr: BufferConfig[],
      key: "checkInBuffer" | "checkOutBuffer",
    ) =>
      arr.reduce(
        (total, b) => total + parseTimeToMinutes(b[key] || "00:00"),
        0,
      );

    const formatHours = (minutes: number) => `${Math.floor(minutes / 60)} hrs`;

    const accommodationMinutes = sumBuffers(
      accommodationBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    const transportMinutes = sumBuffers(
      transportBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    let travelMinutes = 0;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity =
          prevVenue?.venueId && venue.venueId
            ? getIntercityCheckout(
                intercityCheckouts,
                prevVenue.venueId,
                venue.venueId,
                new Date(prevVenue.toDate),
                getTourType(),
              )
            : null;

        if (intercity?.travelTime) {
          travelMinutes = getTravelTimeMinutes(intercity.travelTime);
        }
      }
    }

    const totalMinutes =
      (isIntercityCase
        ? accommodationMinutes
        : type === "checkin"
          ? buffersData.checkIn
          : buffersData.checkOut) + travelMinutes;

    const rawBaseTime =
      type === "checkin"
        ? index === 0
          ? formData.arrivalTime
          : venue.fromTime
        : isLast
          ? formData.departureTime
          : intercityTime || venue.toTime;

    const baseTime = normalizeTime(rawBaseTime || "");

    return {
      venueName,
      baseTime,
      accommodation: formatHours(accommodationMinutes),
      transport: formatHours(transportMinutes),
      totalBuffer: formatHours(totalMinutes),
      hasTransport: !isIntercityCase && transportMinutes > 0,
      usedIntercity: intercityUsed,
      travel:
        travelMinutes > 0
          ? formatHours(travelMinutes)
          : getTourType() === TourType.MIQAAT
            ? "1 hrs"
            : null,
    };
  };

  const CalculationBox = ({
    data,
    type,
    index,
    isLast,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    type: "checkin" | "checkout";
    index: number;
    isLast: boolean;
  }) => {
    if (!data) return null;

    const isFirst = index === 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMiddle = !isFirst && !isLast;

    let label: string | null = null;

    if (type === "checkin") {
      if (isFirst) {
        label = "Arrival Time";
      } else {
        label = null;
      }
    } else {
      // checkout
      if (data.usedIntercity) {
        label = "Intercity Checkout";
      } else if (isLast) {
        label = "Departure Time";
      } else {
        label = null;
      }
    }

    return (
      <div
        className="absolute z-10 left-0 mt-2 w-64 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-gray-700 space-y-1 
          opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-lg pointer-events-none"
      >
        <div className="font-medium text-blue-700">Calculation Breakdown</div>
        {label && (
          <div>
            {label}: <span className="font-semibold">{data.baseTime}</span>
          </div>
        )}

        <div>
          Accommodation Buffer:{" "}
          <span className="font-semibold">{data.accommodation}</span>
        </div>

        {data.hasTransport && (
          <div>
            Transport Buffer:{" "}
            <span className="font-semibold">{data.transport}</span>
          </div>
        )}

        {data.travel && (
          <div>
            Travel Time: <span className="font-semibold">{data.travel}</span>
          </div>
        )}

        <div className="pt-1 border-t">
          Total Buffer:{" "}
          <span className="font-semibold text-blue-700">
            {data.totalBuffer}
          </span>
        </div>
      </div>
    );
  };

  const mapApiToState = (data: TourDetailApi) => {
    setArrivalConfirmedFromApi(data.isArrivalConfirmed ?? false);
    setDepartureConfirmedFromApi(data.isDepartureConfirmed ?? false);
    const mappedVenues = (data.venueSchedules || []).map((v, i) => ({
      id: v.publicId || `venue-${i}`,
      venueId: v.venueId,
      fromDate: formatDateSafe(v.checkInDate),
      fromTime: v.checkInTime || "",
      toDate: formatDateSafe(v.checkOutDate),
      toTime: v.checkOutTime || "",
    }));

    setFormData((prev) => ({
      ...prev,
      arrivalAirport: data.arrivalPortId || 0,
      arrivalDate: data.arrivalDate?.split("T")[0] || "",
      arrivalTime: data.arrivalTime || "",
      arrivalFlightNumber: data.arrivalFlightNo || "",
      arrivalBookingRef: data.arrivalBookingRef || "",
      arrivalAirline: data.arrivalAirline || "",
      arrivalConfirmed: data.isArrivalConfirmed ?? false,

      departureAirport: data.departurePortId || 0,
      departureDate: data.departureDate?.split("T")[0] || "",
      departureTime: data.departureTime || "",
      departureFlightNumber: data.departureFlightNo || "",
      departureBookingRef: data.departureBookingRef || "",
      departureAirline: data.departureAirline || "",
      departureConfirmed: data.isDepartureConfirmed ?? false,

      tourName: data.tourName || "",
      noOfApplicants: String(data.noOfApplicants || ""),
      faizAccommodation: data.faizAccommodation ? "yes" : "no",

      accommodationPreference:
        data.accommodationPreference === 1 ? "minimum" : "maximum",

      specialAccommodationRequest:
        data.specialAccommodationRequest || "",

      venues: mappedVenues,
      tourType: String(data.tourType || ""),
    }));

    if (data.faizAccommodation) {
      setAccommodateBy(
        data.accommodationPreference === 1 ? "minimum" : "maximum"
      );

      setSpecialRoomRequestDetails(
        data.specialAccommodationRequest || ""
      );
    } else {
      setAccommodateBy("maximum");
      setSpecialRoomRequestDetails("");
    }
    
    setTourStatus(data.status);
    setIsInitialized(true);
  };

  const getStatusMeta = (status: number | null) => {
    switch (status) {
      case 1:
        return {
          label: "Pending Approval",
          className: "bg-orange-100 text-orange-700 border-orange-300",
        };
      case 2:
        return {
          label: "✓ Venue Approved",
          className: "bg-green-100 text-green-700 border-green-300",
        };
      case 3:
        return {
          label: "✕ Rejected",
          className: "bg-red-100 text-red-700 border-red-300",
        };
      case 4:
        return {
          label: "date-changed",
          className: "bg-amber-100 text-amber-700 border-amber-300",
        };
      default:
        return {
          label: "date-changed",
          className: "bg-amber-100 text-amber-700 border-amber-300",
        };
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const statusMeta = getStatusMeta(tourStatus);

  const formatLocalDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const isDateDisabled = (date: Date, type: "arrival" | "departure") => {
    const current = new Date(date);
    current.setHours(0, 0, 0, 0);

    if (selectedMiqaat?.type === "package") {
      const rotationStart = new Date(selectedMiqaat.arrivalDate.split("T")[0]);

      const rotationEnd = new Date(selectedMiqaat.departureDate.split("T")[0]);

      rotationStart.setHours(0, 0, 0, 0);
      rotationEnd.setHours(0, 0, 0, 0);

      return current < rotationStart || current > rotationEnd;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minBookingDate = new Date(today);
    minBookingDate.setDate(today.getDate() + 3);

    if (current < minBookingDate) {
      return true;
    }

    if (operatorExpiryDate) {
      const expiry = new Date(operatorExpiryDate);
      expiry.setHours(0, 0, 0, 0);

      if (current > expiry) {
        return true;
      }
    }

    return false;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSave = async () => {
    try {
      setLoading(true);
      if (!id) {
        toast.error("Missing tour ID");
        return;
      }

      const payload = buildUpdatePayload();

      console.log("UPDATE PAYLOAD:", payload);

      await updateTour(id, payload);

      toast.success("Tour updated successfully!");
      navigate("/tours", { state: { refresh: true } });
    } catch (err) {
      console.error(err);
      toast.error("Failed to update tour");
    } finally {
      setLoading(false);
    }
  };

  const getTourTypeLabel = () => {
    const category = getTourCategory();

    switch (category) {
      case 2:
        return "Miqaat Tour";

      case 3:
        return "Faiz Tour";

      case 4:
        return "External Tour";

      default:
        return "-";
    }
  };

  const isOutsideTourRange = (date: Date) => {
    if (!formData.arrivalDate || !formData.departureDate) return false;

    const d = new Date(date);
    const start = new Date(formData.arrivalDate);
    const end = new Date(formData.departureDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);

    return d < start || d > end;
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b pb-4 sm:px-6 sm:py-4 px-0">
          <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* LEFT */}
            <div className="flex items-start sm:items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  try {
                    setLoading(true);
                    navigate(`/tours?bookingFilter=${previousBookingFilter}`);
                  } finally {
                    setLoading(false);
                  }
                }}
                className="h-9 w-9 shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>

              <div>
                <h1
                  className="text-lg sm:text-xl font-semibold"
                  style={{ color: "#5B9BD5" }}
                >
                  Tour Details
                </h1>

                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-snug">
                  {formData.tourName}
                </p>
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Badge
                className={`px-4 py-2 ${
                  approvalStatus === "approved"
                    ? "bg-green-100 text-green-700 border-green-300"
                    : approvalStatus === "recommended"
                      ? "bg-yellow-100 text-yellow-700 border-yellow-300"
                      : approvalStatus === "re-requested"
                        ? "bg-blue-100 text-blue-700 border-blue-300"
                        : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                {approvalStatus === "approved"
                  ? "✓ Approved"
                  : approvalStatus === "recommended"
                    ? "📅 Recommended Dates"
                    : approvalStatus === "re-requested"
                      ? "🔄 Re-Requested"
                      : "💾 Saved"}
              </Badge>

              <Button
                variant="outline"
                onClick={() => navigate(`/tours?bookingFilter=${previousBookingFilter}`)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto sm:px-6 sm:pt-6">
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-0 mb-6 border-b border-border">
            {[
              {
                id: "arrival-departure",
                label: "Arrival & Departure",
                icon: Plane,
              },
              { id: "venue-summary", label: "Venue Summary", icon: FileText },
              { id: "confirmation", label: "Confirmation", icon: FileText },
            ].map(({ id, label, icon: Icon }, index) => {
              const isEnabled = true;

              return (
                <button
                  key={id}
                  onClick={() => {
                    if (isEnabled) {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      setActiveTab(id as any);
                    }
                  }}
                  disabled={!isEnabled}
                  className={`
                    flex items-center gap-2 px-5 py-3 relative text-sm
                    ${activeTab === id ? "text-primary" : "text-muted-foreground"}
                    ${
                      !isEnabled
                        ? "opacity-40 cursor-not-allowed"
                        : "hover:text-foreground"
                    }
                  `}
                >
                  <div
                    className={`
                      w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs
                      ${
                        activeTab === id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }
                    `}
                  >
                    {index + 1}
                  </div>

                  <span className="font-medium">{label}</span>

                  {activeTab === id && isEnabled && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div>
            {/* Tab 1: Arrival & Departure */}

            {activeTab === "arrival-departure" && (
              <div className="space-y-5">
                {/* Recommended Dates Section (shown when status is recommended) */}
                {approvalStatus === "recommended" && (
                  <Card
                    className="border-2 border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base text-yellow-900 flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-yellow-600" />
                            Recommended Alternative Dates
                          </CardTitle>
                          <p className="text-xs text-yellow-700 mt-1">
                            Rooms are available on these dates. Select one to
                            automatically update arrival and departure dates.
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {recommendedDates.map((dateOption) => (
                        <div
                          key={dateOption.id}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedRecommendedDate === dateOption.id
                              ? "border-yellow-500 bg-yellow-100"
                              : "border-yellow-200 bg-white hover:border-yellow-400 hover:bg-yellow-50"
                          }`}
                          onClick={() =>
                            setSelectedRecommendedDate(dateOption.id)
                          }
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="grid grid-cols-2 gap-4 mb-3">
                                <div>
                                  <Label className="text-xs font-semibold text-gray-600">
                                    Arrival
                                  </Label>
                                  <p className="text-sm font-medium text-gray-900">
                                    {new Date(
                                      dateOption.arrivalDate,
                                    ).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}{" "}
                                    at {dateOption.arrivalTime}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-xs font-semibold text-gray-600">
                                    Departure
                                  </Label>
                                  <p className="text-sm font-medium text-gray-900">
                                    {new Date(
                                      dateOption.departureDate,
                                    ).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}{" "}
                                    at {dateOption.departureTime}
                                  </p>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs text-gray-700">
                                  <strong>Reason:</strong> {dateOption.reason}
                                </p>
                                <div className="flex gap-2 flex-wrap">
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    Deluxe: {dateOption.roomsAvailable.deluxe}{" "}
                                    rooms
                                  </Badge>
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                                    Standard:{" "}
                                    {dateOption.roomsAvailable.standard} rooms
                                  </Badge>
                                  <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                                    Shared Gents:{" "}
                                    {dateOption.roomsAvailable.sharedGents}{" "}
                                    rooms
                                  </Badge>
                                  <Badge className="bg-pink-100 text-pink-700 text-xs">
                                    Shared Ladies:{" "}
                                    {dateOption.roomsAvailable.sharedLadies}{" "}
                                    rooms
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAcceptRecommendedDate(dateOption);
                              }}
                              style={{ background: "#10B981" }}
                              className="text-white"
                              size="sm"
                            >
                              <Check className="h-4 w-4 mr-2" />
                              Accept Dates
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Arrival and Departure Cards Side by Side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  {/* Arrival Card */}
                  <Card
                    className="bg-white border-2 border-primary/40 shadow-sm"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="bg-card" style={{ gap: "0px" }}>
                      <div className="flex flex-wrap gap-2 items-center">
                        <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                          <ArrowRight className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <CardTitle className="text-base font-semibold text-foreground">
                          Arrival Details
                        </CardTitle>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-4 bg-card">
                      {/* Row 1 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <SelectFieldset label="Date" required>
                            <CustomDayPicker
                              value={formData.arrivalDate}
                              operatorExpiryDate={
                                operatorExpiryDate ?? undefined
                              }
                              disabledDates={allBlockedDates}
                              minDate={
                                formData.arrivalDate
                                  ? new Date(formData.arrivalDate)
                                  : undefined
                              }
                              disabled={(date) =>
                                formData.arrivalConfirmed ||
                                isDateDisabled(date, "arrival")
                              }
                              isDisabled={formData.arrivalConfirmed}
                              onChange={(date) => {
                                const formatted = formatLocalDate(date);

                                setIsVenueManuallyChanged(true);

                                setFormData((prev) => ({
                                  ...prev,
                                  arrivalDate: formatted,

                                  // Clear departure date if arrival becomes greater
                                  departureDate:
                                    prev.departureDate &&
                                    new Date(formatted) >
                                      new Date(prev.departureDate)
                                      ? ""
                                      : prev.departureDate,
                                }));

                                setRefreshKey((k) => k + 1);
                              }}
                            />
                          </SelectFieldset>
                        </div>
                        <div>
                          <InputFieldset
                            label="Flight No."
                            required
                            placeholder="IQ 101"
                            className="text-xs"
                            value={formData.arrivalFlightNumber}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                arrivalFlightNumber: e.target.value,
                              }))
                            }
                            disabled={formData.arrivalConfirmed}
                          />
                        </div>
                      </div>

                      {/* Row 2 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <InputFieldset
                            label="Time"
                            required
                            type="time"
                            className="text-xs"
                            value={formData.arrivalTime}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                arrivalTime: e.target.value,
                              }))
                            }
                            disabled={formData.arrivalConfirmed}
                          />
                        </div>
                        <div>
                          <SelectFieldset label="Airport" required>
                            <Select
                              value={formData.arrivalAirport?.toString()}
                              onValueChange={(value) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  arrivalAirport: Number(value),
                                }))
                              }
                              disabled={formData.arrivalConfirmed}
                            >
                              <SelectTriggerUI className="text-xs">
                                <SelectValue placeholder="Select..." />
                              </SelectTriggerUI>

                              <SelectContent>
                                {ports.map((port) => (
                                  <SelectItem
                                    key={port.portId}
                                    value={port.portId.toString()}
                                  >
                                    {port.portName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </SelectFieldset>
                        </div>
                      </div>

                      {/* Row 3 */}
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                        <div>
                          <InputFieldset
                            label="Airline"
                            placeholder="Iraqi Airways"
                            className="text-xs"
                            value={formData.arrivalAirline}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                arrivalAirline: e.target.value,
                              }))
                            }
                            disabled={formData.arrivalConfirmed}
                          />
                        </div>

                        <div>
                          <InputFieldset
                            label="Booking Ref"
                            placeholder="KUN-ARR"
                            className="text-xs"
                            value={formData.arrivalBookingRef}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                arrivalBookingRef: e.target.value,
                              }))
                            }
                            disabled={formData.arrivalConfirmed}
                          />
                        </div>

                        <div className="pb-0.5">
                          <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                            Confirmed
                          </Label>

                          <div className="flex items-center h-9">
                            {arrivalConfirmedFromApi ? (
                              <Badge className="bg-green-100 text-green-700 border-green-300">
                                ✓ Yes
                              </Badge>
                            ) : (
                              <Switch
                                checked={formData.arrivalConfirmed}
                                onCheckedChange={(checked) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalConfirmed: checked,
                                  }))
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Departure Card */}
                  <Card
                    className="bg-white border-2 border-destructive/40 shadow-sm"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="bg-card" style={{ gap: "0px" }}>
                      <div className="flex flex-wrap gap-2 items-center">
                        <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                          <Plane className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <CardTitle className="text-base font-semibold text-foreground">
                          Departure Details
                        </CardTitle>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-4 bg-card">
                      {/* Row 1 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <SelectFieldset label="Date" required>
                            <CustomDayPicker
                              value={formData.departureDate}
                              operatorExpiryDate={
                                operatorExpiryDate ?? undefined
                              }
                              disabledDates={allBlockedDates}
                              minDate={
                                formData.arrivalDate
                                  ? new Date(formData.arrivalDate)
                                  : undefined
                              }
                              disabled={(date) =>
                                formData.departureConfirmed ||
                                isDateDisabled(date, "departure")
                              }
                              isDisabled={formData.departureConfirmed}
                              onChange={(date: Date) => {
                                const formatted = formatLocalDate(date);

                                setIsVenueManuallyChanged(true);

                                setFormData((prev) => ({
                                  ...prev,
                                  departureDate: formatted,
                                }));

                                setRefreshKey((k) => k + 1);
                              }}
                            />
                          </SelectFieldset>
                        </div>
                        <div>
                          <InputFieldset
                            label="Flight No."
                            required
                            placeholder="IQ 202"
                            className="text-xs"
                            value={formData.departureFlightNumber}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                departureFlightNumber: e.target.value,
                              }))
                            }
                            disabled={formData.departureConfirmed}
                          />
                        </div>
                      </div>

                      {/* Row 2 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <InputFieldset
                            label="Time"
                            required
                            type="time"
                            className="text-xs"
                            value={formData.departureTime}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                departureTime: e.target.value,
                              }))
                            }
                            disabled={formData.departureConfirmed}
                          />
                        </div>
                        <div>
                          <SelectFieldset label="Airport" required>
                            <Select
                              value={formData.departureAirport?.toString()}
                              onValueChange={(value) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  departureAirport: Number(value),
                                }))
                              }
                              disabled={formData.departureConfirmed}
                            >
                              <SelectTriggerUI className="text-xs">
                                <SelectValue placeholder="Select..." />
                              </SelectTriggerUI>

                              <SelectContent>
                                {ports.map((port) => (
                                  <SelectItem
                                    key={port.portId}
                                    value={port.portId.toString()}
                                  >
                                    {port.portName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </SelectFieldset>
                        </div>
                      </div>

                      {/* Row 3 */}
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                        <div>
                          <InputFieldset
                            label="Booking Ref"
                            placeholder="KUN-DEP"
                            className="text-xs"
                            value={formData.departureBookingRef}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                departureBookingRef: e.target.value,
                              }))
                            }
                            disabled={formData.departureConfirmed}
                          />
                        </div>

                        <div>
                          <InputFieldset
                            label="Airline"
                            placeholder="Iraqi Airways"
                            className="text-xs"
                            value={formData.departureAirline}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                departureAirline: e.target.value,
                              }))
                            }
                            disabled={formData.departureConfirmed}
                          />
                        </div>

                        <div className="pb-0.5">
                          <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                            Confirmed
                          </Label>

                          <div className="flex items-center h-9">
                            {departureConfirmedFromApi ? (
                              <Badge className="bg-green-100 text-green-700 border-green-300">
                                ✓ Yes
                              </Badge>
                            ) : (
                              <Switch
                                checked={formData.departureConfirmed}
                                onCheckedChange={(checked) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    departureConfirmed: checked,
                                  }))
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Tab 2: Venue Summary */}
            {activeTab === "venue-summary" && (
              <div className="space-y-5">
                <Card
                  className="bg-white border border-border shadow-sm"
                  style={{ gap: "0px" }}
                >
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold">
                      Venue Summary
                    </CardTitle>
                  </CardHeader>

                  <CardContent style={{ padding: "0px 24px 20px 24px" }}>
                    <div
                      className="grid grid-cols-1 md:grid-cols-12 gap-3 border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                      style={{ paddingLeft: "16px", paddingRight: "16px" }}
                    >
                      {/* Tour Name */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                          label="Tour Name"
                          required
                          placeholder="Enter tour name"
                          className="text-sm focus:ring-2 focus:ring-primary/30"
                          value={formData.tourName}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              tourName: e.target.value,
                            }))
                          }
                        />
                        {errors.tourName && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.tourName}
                          </p>
                        )}
                      </div>

                      {/* Applicants */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                          label="No. of Applicants"
                          required
                          type="number"
                          placeholder="0"
                          className="text-sm focus:ring-2 focus:ring-primary/30"
                          value={formData.noOfApplicants}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              noOfApplicants: e.target.value,
                            }))
                          }
                        />
                        {errors.noOfApplicants && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.noOfApplicants}
                          </p>
                        )}
                      </div>

                      {/* Parent Tour Id */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                            label="Parent Tour Id"
                            placeholder="Enter Parent Tour Id"
                            className="text-sm"
                            maxLength={50}
                            value={formData.parentTourId}
                            onChange={(e) => {
                            const value = e.target.value.toUpperCase();

                            // Allow only letters, numbers and hyphen
                            if (!/^[A-Z0-9-]*$/.test(value)) {
                                return;
                            }

                            if (value.length > 50) {
                                return;
                            }

                            setFormData((prev) => ({
                                ...prev,
                                parentTourId: value,
                            }));

                            // Clear error while user is correcting the value
                            setErrors((prev) => ({
                                ...prev,
                                parentTourId: "",
                            }));
                            }}
                        />

                        {errors.parentTourId && (
                            <p className="text-xs text-red-500 mt-1">
                            {errors.parentTourId}
                            </p>
                        )}
                      </div>

                      {/* Faiz */}
                      <div className="md:col-span-3 space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground">
                          Faiz Accommodation{" "}
                          <span className="text-red-500">*</span>
                        </Label>
                        <RadioGroup
                          value={formData.faizAccommodation}
                          disabled
                          className="flex gap-6 items-center h-10"
                        >
                          <div className="flex items-center gap-2">
                            <RadioGroupItem
                              value="yes"
                              id="faiz-yes"
                              className="
                                                  h-5 w-5 rounded-full
                                                  border-2 border-gray-200
                                                  flex items-center justify-center
                                                  data-[state=checked]:border-primary
                                                "
                            >
                              <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                            </RadioGroupItem>
                            <Label
                              htmlFor="faiz-yes"
                              className="text-sm cursor-pointer"
                            >
                              Yes
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem
                              value="no"
                              id="faiz-no"
                              className="
                                                  h-5 w-5 rounded-full
                                                  border-2 border-gray-200
                                                  flex items-center justify-center
                                                  data-[state=checked]:border-primary
                                                "
                            >
                              <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                            </RadioGroupItem>
                            <Label
                              htmlFor="faiz-no"
                              className="text-sm cursor-pointer"
                            >
                              No
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>

                    {formData.faizAccommodation === "yes" && (
                      <Card className="bg-white border-blue-200 bg-blue-50/50 mt-6 mb-6">
                        <CardContent style={{ padding: "16px" }}>
                          {/* Occupancy Question */}
                          <div className="mb-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
                            <div className="lg:col-span-6">
                              <Label className="text-sm font-medium text-gray-900 block">
                                Want to accommodate based on Min or Max Occupancy?
                              </Label>
                            </div>

                            <div className="lg:col-span-6">
                              <RadioGroup
                                value={accommodateBy || ""}
                                onValueChange={(v) =>
                                  setAccommodateBy(v as "minimum" | "maximum")
                                }
                                className="flex gap-6 items-center"
                              >
                                {/* Minimum */}
                                <div className="flex items-center gap-2">
                                  <RadioGroupItem
                                    value="minimum"
                                    id="min-occupancy"
                                    className="
                                      h-5 w-5 rounded-full
                                      border-2 border-gray-200
                                      flex items-center justify-center
                                      data-[state=checked]:border-primary
                                    "
                                  >
                                    <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                                  </RadioGroupItem>

                                  <Label
                                    htmlFor="min-occupancy"
                                    className="text-sm cursor-pointer"
                                  >
                                    Min Occupancy
                                  </Label>
                                </div>

                                {/* Maximum */}
                                <div className="flex items-center gap-2">
                                  <RadioGroupItem
                                    value="maximum"
                                    id="max-occupancy"
                                    className="
                                      h-5 w-5 rounded-full
                                      border-2 border-gray-200
                                      flex items-center justify-center
                                      data-[state=checked]:border-primary
                                    "
                                  >
                                    <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                                  </RadioGroupItem>

                                  <Label
                                    htmlFor="max-occupancy"
                                    className="text-sm cursor-pointer"
                                  >
                                    Max Occupancy
                                  </Label>
                                </div>
                              </RadioGroup>
                            </div>
                          </div>
                          <InputFieldset
                            label="Any special accommodation request?"
                            required
                            placeholder="e.g. Wheelchair accessible, Ground floor, Adjacent rooms..."
                            className="text-sm"
                            value={specialRoomRequestDetails}
                            onChange={(e) =>
                              setSpecialRoomRequestDetails(e.target.value)
                            }
                          />
                        </CardContent>
                      </Card>
                    )}

                    {/* ================= VENUE SECTION ================= */}
                    <div className="space-y-4" style={{ marginTop: "14px" }}>
                      {/* Section Header */}
                      <div
                        className="flex"
                        style={{
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">
                            Venue Schedule{" "}
                            <span className="text-red-500">*</span>
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            Add venues with check-in and check-out details
                          </p>
                        </div>
                        {/* Add Button */}
                        <div
                          className="flex justify-end"
                          style={{ marginTop: "20px" }}
                        >
                          <Button
                            type="button"
                            className="h-10 px-4 text-sm font-medium"
                            onClick={() => {
                              const newVenue = {
                                id: `venue-${Date.now()}`,
                                venueId: null,
                                fromDate: "",
                                fromTime: "",
                                toDate: "",
                                toTime: "",
                              };

                              setFormData((prev) => {
                                const updated = [...prev.venues];
                                updated.splice(1, 0, newVenue);

                                return {
                                  ...prev,
                                  venues: updated,
                                };
                              });
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Venue
                          </Button>
                        </div>
                      </div>

                      {/* Empty State */}
                      {formData.venues.length === 0 ? (
                        <div className="border border-dashed border-border rounded-lg p-10 text-center">
                          <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                          <p className="text-sm text-muted-foreground">
                            No venues added yet
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {formData.venues.map((venue, index) => {
                            const isFirst = index === 0;
                            const isLast = index === formData.venues.length - 1;
                            return (
                              <div
                                key={venue.id}
                                className="border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                              >
                                <div className="flex gap-4">
                                  {/* <div className="w-7 h-7 flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                                                                    {index + 1}
                                                                  </div> */}
                                  <div className="flex-1 space-y-4">
                                    {/* Venue Name */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                      <div className="space-y-1">
                                        <SelectFieldset
                                          label={`Venue Name ${index + 1}`}
                                          required
                                          error={errors[`venueName-${index}`]}
                                          className="max-w-xs"
                                        >
                                          <Select
                                            value={
                                              venue.venueId
                                                ? venue.venueId.toString()
                                                : ""
                                            }
                                            onValueChange={(value) => {
                                              const updated = [
                                                ...formData.venues,
                                              ];
                                              updated[index].venueId =
                                                Number(value);
                                              setIsVenueManuallyChanged(false);
                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                          >
                                            <SelectTriggerUI className="h-10 text-sm">
                                              <SelectValue placeholder="Select venue">
                                                {venue.venueId
                                                  ? venuesFromApi.find(
                                                      (v) =>
                                                        v.venueId ===
                                                        venue.venueId,
                                                    )?.venueName
                                                  : "Select venue"}
                                              </SelectValue>
                                            </SelectTriggerUI>

                                            <SelectContent>
                                              {venuesFromApi.map((v) => (
                                                <SelectItem
                                                  key={v.venueId}
                                                  value={v.venueId.toString()}
                                                >
                                                  {v.venueName}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </SelectFieldset>
                                      </div>
                                    </div>

                                    {/* Dates */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                      {/* Check-In Date */}
                                      <div className="space-y-1">
                                        <SelectFieldset
                                          label="Check-In Date"
                                          required
                                          error={errors[`fromDate-${index}`]}
                                        >
                                          <CustomDayPicker
                                            value={venue.fromDate}
                                            operatorExpiryDate={
                                              operatorExpiryDate ?? undefined
                                            }
                                            mode="range"
                                            disabledDates={allBlockedDates}
                                            highlightRange={{
                                              from: formData.arrivalDate,
                                              to: formData.departureDate,
                                            }}
                                            disabled={(date) =>
                                              isFirst ||
                                              isDateDisabled(date, "arrival") ||
                                              isOutsideTourRange(date)
                                            }
                                            onChange={(date: Date) => {
                                              if (isFirst) return;

                                              const formatted =
                                                formatLocalDate(date);

                                              const updated = [
                                                ...formData.venues,
                                              ];
                                              updated[index].fromDate =
                                                formatted;

                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                            placeholder="dd-MM-yyyy"
                                          />
                                        </SelectFieldset>
                                      </div>

                                      {/* Check-In Time */}
                                      <div className="space-y-1">
                                        <div className="relative group">
                                          <InputFieldset
                                            label="Check-In Time"
                                            required
                                            type="time"
                                            className={`text-sm pr-8 border border-gray-300 rounded-md transition-all outline-none ${
                                              isFirst
                                                ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                                : ""
                                            }`}
                                            value={venue.fromTime}
                                            disabled
                                            onChange={(e) => {
                                              if (isFirst) return;

                                              const updated = [
                                                ...formData.venues,
                                              ];
                                              updated[index].fromTime =
                                                e.target.value;

                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                          />
                                          <CalculationBox
                                            type="checkin"
                                            index={index}
                                            isLast={
                                              index ===
                                              formData.venues.length - 1
                                            }
                                            data={getCalculationBreakdown(
                                              venue,
                                              index,
                                              "checkin",
                                            )}
                                          />
                                        </div>
                                        {errors[`fromTime-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`fromTime-${index}`]}
                                          </p>
                                        )}
                                      </div>

                                      {/* Check-Out Date */}
                                      <div className="space-y-1">
                                        <SelectFieldset
                                          label="Check-Out Date"
                                          required
                                          error={errors[`toDate-${index}`]}
                                        >
                                          <CustomDayPicker
                                            value={venue.toDate}
                                            operatorExpiryDate={
                                              operatorExpiryDate ?? undefined
                                            }
                                            mode="range"
                                            highlightRange={{
                                              from: formData.arrivalDate,
                                              to: formData.departureDate,
                                            }}
                                            disabledDates={allBlockedDates}
                                            disabled={(date) =>
                                              isLast ||
                                              isDateDisabled(
                                                date,
                                                "departure",
                                              ) ||
                                              isOutsideTourRange(date)
                                            }
                                            onChange={(date: Date) => {
                                              const formatted =
                                                formatLocalDate(date);

                                              setIsVenueManuallyChanged(true);

                                              setFormData((prev) => {
                                                const updated = [
                                                  ...prev.venues,
                                                ];
                                                updated[index].toDate =
                                                  formatted;

                                                if (
                                                  index <
                                                  updated.length - 1
                                                ) {
                                                  updated[index + 1].fromDate =
                                                    formatted;
                                                }

                                                return {
                                                  ...prev,
                                                  venues: updated,
                                                };
                                              });
                                            }}
                                            placeholder="dd-MM-yyyy"
                                          />
                                        </SelectFieldset>
                                      </div>

                                      {/* Check-Out Time */}
                                      <div className="space-y-1">
                                        <div className="relative group">
                                          <InputFieldset
                                            label="Check-Out Time"
                                            required
                                            type="time"
                                            className={`text-sm border border-gray-300 rounded-md transition-all outline-none ${
                                              isLast
                                                ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                                : ""
                                            }`}
                                            value={venue.toTime}
                                            disabled
                                            onChange={(e) => {
                                              if (isLast) return;

                                              setIsVenueManuallyChanged(true);

                                              setFormData((prev) => {
                                                const updated = [
                                                  ...prev.venues,
                                                ];

                                                updated[index].toTime =
                                                  e.target.value;
                                                if (
                                                  index <
                                                  updated.length - 1
                                                ) {
                                                  updated[index + 1].fromTime =
                                                    e.target.value;
                                                }

                                                return {
                                                  ...prev,
                                                  venues: updated,
                                                };
                                              });
                                            }}
                                          />
                                          <CalculationBox
                                            type="checkout"
                                            index={index}
                                            isLast={
                                              index ===
                                              formData.venues.length - 1
                                            }
                                            data={getCalculationBreakdown(
                                              venue,
                                              index,
                                              "checkout",
                                            )}
                                          />
                                        </div>
                                        {errors[`toTime-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`toTime-${index}`]}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Delete */}
                                  {!(isFirst || isLast) && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                      onClick={() => {
                                        const updated = formData.venues.filter(
                                          (_, i) => i !== index,
                                        );

                                        setIsVenueManuallyChanged(false); // allow auto recalculation

                                        setFormData((prev) => ({
                                          ...prev,
                                          venues: updated,
                                        }));
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Tab 3: Confirmation */}
            {activeTab === "confirmation" && (
              <div className="space-y-5">
                {/* Summary Section */}
                <div className="space-y-5">
                  <Card className="bg-white w-full sm:w-4/5 md:w-3/5 mx-auto">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        {/* Tour Name */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Tour Name
                          </span>
                          <span
                            className="text-xs text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {formData.tourName}
                          </span>
                        </div>

                        {/* Package */}
                        {/* <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="
                            text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Package
                          </span>
                          <span
                            className="text-xs text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {formData.duration || "-"}
                          </span>
                        </div> */}

                        {/* Tour Type */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Tour Type
                          </span>
                          <span
                            className="text-xs font-semibold text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {getTourTypeLabel()}
                          </span>
                        </div>

                        {/* Applicants */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Applicants
                          </span>
                          <span
                            className="text-xs text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {formData.noOfApplicants} Applicants
                          </span>
                        </div>

                        {/* Faiz Accommodation */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Faiz Accommodation
                          </span>
                          <span
                            className="text-xs font-semibold text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {formData.faizAccommodation === "yes"
                              ? "Yes"
                              : "No"}
                          </span>
                        </div>

                        {/* Arrival */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Arrival
                          </span>
                          <div style={{ width: "50%" }}>
                            <div className="text-xs text-gray-900">
                              {formData.arrivalDate} •
                              {
                                ports.find(
                                  (p) => p.portId === formData.arrivalAirport,
                                )?.portName
                              }
                            </div>

                            <div className="text-[10px] text-gray-600 mt-0.5">
                              Flight {formData.arrivalFlightNumber} •
                              {formData.arrivalTime}
                            </div>
                          </div>
                        </div>

                        {/* Departure */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Departure
                          </span>
                          <div style={{ width: "50%" }}>
                            <div className="text-xs text-gray-900">
                              {formData.departureDate} •
                              {
                                ports.find(
                                  (p) => p.portId === formData.departureAirport,
                                )?.portName
                              }
                            </div>

                            <div className="text-[10px] text-gray-600 mt-0.5">
                              Flight {formData.departureFlightNumber} •
                              {formData.departureTime}
                            </div>
                          </div>
                        </div>

                        {/* Venues */}
                        <div className="flex items-start justify-between py-2">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Venues
                          </span>
                          <div className="space-y-0.5" style={{ width: "50%" }}>
                            {formData.venues.map((venue, index) => (
                              <div
                                key={index}
                                className="text-xs text-gray-900"
                              >
                                {
                                  venuesFromApi.find(
                                    (v) => v.venueId === venue.venueId,
                                  )?.venueName
                                }{" "}
                                •{venue.fromDate} → {venue.toDate}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Notes Section */}
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 mb-3">
                    Notes
                  </h2>

                  <Card className="bg-white">
                    <CardContent style={{ padding: "20px" }}>
                      <textarea
                        placeholder="Additional instructions..."
                        className="w-full min-h-[100px] p-2.5 text-xs border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        defaultValue=""
                      />
                    </CardContent>
                  </Card>
                </div>

                {/* Confirmation */}
                <Card className="bg-white">
                  <CardContent style={{ padding: "20px" }}>
                    <label
                      className="flex items-start gap-2.5 cursor-pointer"
                      style={{ alignItems: "center" }}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-700">
                        I confirm all tour details are accurate and complete
                      </span>
                    </label>
                  </CardContent>
                </Card>

                {/* Submit Button */}
                <div className="flex justify-end pt-2">
                  <Button
                    style={{ background: "#5B9BD5" }}
                    className="text-white px-8 h-9 text-sm"
                    onClick={() => {
                      setApprovalStatus("re-requested");
                      navigate(`/tours/tour-approval/${id}`);
                      toast.success(
                        "Tour submitted successfully for final approval!",
                      );
                    }}
                  >
                    Send for Approval 
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            {currentIndex > 0 ? (
              <Button
                variant="outline"
                onClick={() =>
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  setActiveTab(enabledTabs[currentIndex - 1] as any)
                }
                className="bg-white text-sm h-9"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            ) : (
              <div />
            )}

            <div className="text-xs text-muted-foreground font-medium">
              {currentIndex + 1} / {enabledTabs.length}
            </div>

            {currentIndex < enabledTabs.length - 1 ? (
              <Button
                onClick={() =>
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  setActiveTab(enabledTabs[currentIndex + 1] as any)
                }
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm h-9"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
